/**
 * PracticePulsePage — Practice Manager command center.
 *
 * Replaces the old /manager/overview (OverviewDashboard).
 * Sections:
 *   1. Topbar   — site selector, Customize (KPI config), Refresh
 *   2. Huddle Brief — AI-generated briefing (HuddleBanner)
 *   3. KPI Cards — 2×3 configurable grid (useUserPreferences)
 *   4. Inbox — AI-powered email client (replaces Operational Focus Tabs)
 *   5. Domain Dashboards — 2×4 accordion grid, single-open (DomainPanelGrid)
 */
import React, { useState } from 'react';
import { Settings, Maximize2, Minimize2 } from 'lucide-react';
import HuddleBanner from './HuddleBanner';
import InboxShell from './inbox/InboxShell';
import DomainPanelGrid from './DomainPanelGrid';
import KPIConfigModal from './KPIConfigModal';
import { KPICard } from '../ui/KPICard';
import { SectionHeading } from '../ui/SectionHeading';
import { TooltipProvider } from '../ui/tooltip';
import { ICON_PX } from '../ui/lib/iconSizes';
import { useUserPreferences, DEFAULT_KPI_SELECTION } from '../../hooks/useUserPreferences';

// ─── KPI data library ──────────────────────────────────────────────────────────
// Mock values keyed by the same ids used in KPIConfigModal / useUserPreferences.
const KPI_DATA = {
  'staffing.coverage':       { label: 'Staffing Coverage',    value: '86%',  trend: '↓ 2% vs last week', trendDown: true,  alert: true,  donut: null },
  'staffing.open_shifts':    { label: 'Open Shifts',          value: '2',    trend: 'PM shift short',     trendDown: true,  alert: true,  donut: null },
  'staffing.overtime':       { label: 'Overtime Hours',       value: '14h',  trend: 'Within budget',      trendDown: false, alert: false, donut: null },
  'staffing.turnover':       { label: 'Turnover Rate (30d)',  value: '4.2%', trend: 'Stable',             trendDown: false, alert: false, donut: null },
  'staffing.fill_rate':      { label: 'Fill Rate',            value: '94%',  trend: '↑ 1% this week',    trendDown: false, alert: false, donut: null },
  'quality.task_completion': { label: 'Task Completion',      value: 73,     trend: '73 of 100 tasks',    trendDown: false, alert: false, donut: { value: 73, total: 100, color: '#2EB87A' } },
  'quality.performance':     { label: 'Quality Performance',  value: '85%',  trend: '↑ 3% vs last month', trendDown: false, alert: false, donut: null },
  'quality.a1c':             { label: 'A1C Control Rate',     value: '68%',  trend: '↓ below 75% target', trendDown: true,  alert: true,  donut: null },
  'quality.hedis':           { label: 'HEDIS Compliance',     value: '91%',  trend: 'Above target',        trendDown: false, alert: false, donut: null },
  'quality.open_compliance': { label: 'Open Compliance',      value: '3',    trend: 'Due this week',       trendDown: false, alert: false, donut: null },
  'access.same_day':         { label: 'Same-Day Access',      value: '78%',  trend: '↓ below 85% target', trendDown: true,  alert: false, donut: null },
  'access.wait_time':        { label: 'Avg Wait Time',        value: '4.2d', trend: 'Stable',             trendDown: false, alert: false, donut: null },
  'access.open_slots':       { label: 'Open Slots Today',     value: '12',   trend: '+3 vs yesterday',    trendDown: false, alert: false, donut: null },
  'access.backlog':          { label: 'Backlog Count',        value: '28',   trend: 'Stable',             trendDown: false, alert: false, donut: null },
  'access.utilization':      { label: 'Appt Utilization',    value: '82%',  trend: 'On target',          trendDown: false, alert: false, donut: null },
  'finance.revenue':         { label: 'Revenue/Patient',     value: '$312', trend: 'Stable',             trendDown: false, alert: false, donut: null },
  'finance.collections':     { label: 'Collections Rate',    value: '88%',  trend: '↓ 2% this month',   trendDown: true,  alert: false, donut: null },
  'finance.denials':         { label: 'Denied Claims',       value: '6.2%', trend: '↑ watch',           trendDown: true,  alert: true,  donut: null },
  'finance.days_ar':         { label: 'Days in AR',          value: '32',   trend: 'Stable',             trendDown: false, alert: false, donut: null },
  'finance.variance':        { label: 'Budget Variance',     value: '-1.2%',trend: 'Within tolerance',   trendDown: false, alert: false, donut: null },
  'consumer.nps':            { label: 'Net Promoter Score',  value: '42',   trend: 'Stable',             trendDown: false, alert: false, donut: null },
  'consumer.satisfaction':   { label: 'Patient Satisfaction',value: '86%',  trend: '↑ 1%',              trendDown: false, alert: false, donut: null },
  'consumer.complaints':     { label: 'Complaints (30d)',    value: '7',    trend: '↓ 3 vs prior',      trendDown: false, alert: false, donut: null },
  'consumer.resolved':       { label: 'Resolved Complaints', value: '79%',  trend: 'Below 85% target',  trendDown: true,  alert: false, donut: null },
  'consumer.return_visits':  { label: 'Return Visit Rate',   value: '61%',  trend: 'On track',           trendDown: false, alert: false, donut: null },
  'practice.active_patients':{ label: 'Active Patients',     value: '1,842',trend: '+12 this month',    trendDown: false, alert: false, donut: null },
  'practice.new_patients':   { label: 'New Patients (MTD)',  value: '38',   trend: 'On track',           trendDown: false, alert: false, donut: null },
  'practice.panel_util':     { label: 'Panel Utilization',   value: '88%',  trend: '↑ 2%',              trendDown: false, alert: false, donut: null },
  'practice.no_show':        { label: 'No-Show Rate',        value: '9.4%', trend: '↑ watch',           trendDown: true,  alert: false, donut: null },
  'practice.referrals':      { label: 'Referral Completion', value: '74%',  trend: 'Below 80% target',  trendDown: true,  alert: false, donut: null },
  'ops.intervention_time':   { label: 'Avg Intervention Time',value: '3.2h', trend: 'Stable',            trendDown: false, alert: false, donut: null },
  'ops.uptime':              { label: 'System Uptime',       value: '99.8%',trend: 'Healthy',           trendDown: false, alert: false, donut: null },
  'ops.response_time':       { label: 'Response Time',       value: '1.8h', trend: 'Stable',            trendDown: false, alert: false, donut: null },
  'ops.escalation_rate':     { label: 'Escalation Rate',     value: '4.3%', trend: 'Stable',            trendDown: false, alert: false, donut: null },
  'ops.reliability_score':   { label: 'Reliability Score',   value: '6.2',  trend: 'Above threshold',   trendDown: false, alert: false, donut: null },
  'pophealth.outreach':      { label: 'Outreach Completion', value: '68%',  trend: '↓ below 75%',      trendDown: true,  alert: true,  donut: null },
  'pophealth.high_risk':     { label: 'High-Risk Pending',   value: '24',   trend: 'Watch',             trendDown: false, alert: false, donut: null },
  'pophealth.care_gaps':     { label: 'Care Gap Closure',    value: '58%',  trend: 'Improving',         trendDown: false, alert: false, donut: null },
  'pophealth.care_plans':    { label: 'Active Care Plans',   value: '187',  trend: 'Stable',            trendDown: false, alert: false, donut: null },
  'pophealth.screenings':    { label: 'Screenings Due',      value: '31',   trend: 'Action needed',     trendDown: true,  alert: false, donut: null },
};

const FALLBACK_KPI = { label: 'KPI', value: '—', trend: null, trendDown: false, alert: false };

// ─── Main page ─────────────────────────────────────────────────────────────────
export default function PracticePulsePage() {
  const { prefs, savePreferences, error: prefsError } = useUserPreferences();
  const [showKPIConfig, setShowKPIConfig] = useState(false);
  const [focusExpanded, setFocusExpanded] = useState(false);

  const selectedKPIs = prefs.kpi_config?.selected ?? DEFAULT_KPI_SELECTION;

  return (
    <TooltipProvider delayDuration={200}>
    <div
      className="dashboard-container"
      style={{
        display: 'flex', flexDirection: 'column',
        gap: 8,
        overflowY: focusExpanded ? 'hidden' : 'auto',
        position: 'relative',
      }}
    >

      {/* ── Huddle Banner ───────────────────────────────────────────────── */}
      <div style={{ flexShrink: 0 }}>
        <HuddleBanner />
      </div>

      {/* ── Key Indicators — horizontal strip ─────────────────────────── */}
      <div style={{ flexShrink: 0 }}>
        <SectionHeading
          title="Key Indicators"
          size="sm"
          className="mb-1.5"
          info="Your top 6 configurable KPIs across all domains. Click the gear icon to choose which metrics appear here."
          aiContext="practice-pulse.kpi-grid"
          action={
            <button
              type="button"
              onClick={() => setShowKPIConfig(true)}
              title="Customize KPIs"
              style={{
                display: 'flex', alignItems: 'center',
                color: 'var(--text-dark-secondary)',
                background: 'none', border: '1px solid var(--surface)',
                borderRadius: 6, padding: '3px 6px', cursor: 'pointer',
              }}
            >
              <Settings size={ICON_PX.md} />
            </button>
          }
        />
        <div style={{
          display: 'grid',
          gridTemplateColumns: `repeat(${selectedKPIs.slice(0, 6).length}, 1fr)`,
          gap: 8,
        }}>
          {selectedKPIs.slice(0, 6).map((id) => {
            const kpi = KPI_DATA[id] || FALLBACK_KPI;
            return (
              <KPICard
                key={id}
                label={kpi.label}
                value={String(kpi.value)}
                trend={kpi.trend ? kpi.trend.replace(/^[↑↓]\s*/, '') : undefined}
                trendDown={kpi.trendDown}
                domain={id.split('.')[0]}
                accentPlacement="top"
                size="sm"
                density="compact"
              />
            );
          })}
        </div>
      </div>

      {/* ── Operational Focus — full-width ─────────────────────────────── */}
      <div
        style={{
          flexShrink: 0, minWidth: 0,
          display: 'flex', flexDirection: 'column',
          height: focusExpanded ? undefined : 520,
          ...(focusExpanded && {
            position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
            zIndex: 10, background: 'var(--surface)',
            height: 'auto',
          }),
        }}
      >
        <SectionHeading
          title="Operational Focus"
          size="sm"
          className="mb-1.5"
          info="Your operational command center. Triage emails, manage your priority queue, organize work on the kanban board, or view scheduled items on the calendar."
          action={
            <button
              type="button"
              onClick={() => setFocusExpanded((v) => !v)}
              title={focusExpanded ? 'Collapse' : 'Expand'}
              style={{
                background: 'none', border: '1px solid var(--surface)',
                borderRadius: 6, padding: '3px 6px', cursor: 'pointer',
                color: 'var(--text-dark-secondary)', display: 'flex', alignItems: 'center',
              }}
            >
              {focusExpanded ? <Minimize2 size={ICON_PX.md} /> : <Maximize2 size={ICON_PX.md} />}
            </button>
          }
        />
        <InboxShell
          style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}
          expanded={focusExpanded}
          variant="smart"
        />
      </div>

      {/* ── Domain Dashboards — 2×4 accordion grid ──────────────────────── */}
      <DomainPanelGrid />

      {/* ── KPI Config Modal ────────────────────────────────────────────── */}
      {showKPIConfig && (
        <KPIConfigModal
          initialSelected={selectedKPIs}
          onSave={(newSelected) => savePreferences({ kpi_config: { selected: newSelected } })}
          onClose={() => setShowKPIConfig(false)}
          saveError={prefsError}
        />
      )}
    </div>
    </TooltipProvider>
  );
}
